<script lang='ts' setup>
import { ref, computed } from 'vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'

import type { Props } from './types'
import { useUserStore } from '@/stores/userStore'
import { getReferralBonus } from '@/utils/referralBonus'

import './_index.scss'

defineProps<Props>()

const userStore = useUserStore()
const { locale } = useI18n()
const { t } = useI18n()

const bonusWithCurrency = computed(() => getReferralBonus(locale.value, true))

const generateReferralUrl = (baseUrl: string, params: Record<string, string> = {}) => {
  const url = new URL(baseUrl)
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value)
  })
  return url
}

const getReferralLink = () => {
  const params: Record<string, string> = { utm_source: 'mgm' }
  if (userStore.user?.id) params.referrer_id = userStore.user.id
  return generateReferralUrl(window.location.href, params).href
}

const createShareAction = (method: 'clipboard' | 'window', urlGenerator?: (link: string) => string) => {
  return () => {
    const referralLink = getReferralLink()
    const targetUrl = urlGenerator ? urlGenerator(referralLink) : referralLink

    if (method === 'clipboard') {
      navigator.clipboard.writeText(targetUrl)
      alert('Enlace copiado al portapapeles')
    } else {
      window.open(targetUrl, '_blank')
    }
  }
}

const shareMethods = {
  whatsapp: {
    text: t('shareMethods.whatsapp'),
    action: createShareAction('window', link =>
      `https://api.whatsapp.com/send?text=${encodeURIComponent(link)}`
    )
  },
  email: {
    text: t('shareMethods.email'),
    action: createShareAction('window', link =>
      `mailto:?subject=${encodeURIComponent('Te comparto esto')}&body=${encodeURIComponent(link)}`
    )
  },
  link: {
    text: t('shareMethods.link'),
    action: createShareAction('clipboard')
  }
}

const shareButtons = Object.entries(shareMethods).map(([id, config]) => ({
  id,
  text: config.text,
  action: config.action
}))

const selectedShareButton = ref<number>()
const handleShareButtonSelect = (idx: number) => {
  selectedShareButton.value = idx
  setTimeout(() => shareButtons[idx].action(), 500)
}
</script>

<script lang='ts'>
export default {
  name: 'AppChatReferralSectionV2'
}
</script>

<template>
  <section class="app-chat-referral-section">
    <section class="app-chat__bubbles app-chat__author-bot referral-banner-in-chat">
      <div class="flex flex-col items-center text-center">
        <h3
          v-sanitize="t('winMoneyText', { bonusWithCurrency })"
          class="text-24 font-normal text-primary-800" />
      </div>

      <div class="flex flex-col gap-8 mt-24">
        <RoomieChipSelector
          v-for="button, idx in shareButtons"
          :id="`app-chat-share-button-${button.id}`"
          :key="`app-chat-message-button-${button.id}`"
          :data-lh-id="`app-chat-share-button-${button.id}`"
          class="app-chat-share-button__chip-selector"
          :label="button.text"
          :is-selected="selectedShareButton === idx"
          @click="handleShareButtonSelect(idx)">
          <template #content>
            <p class="text-14 pointer-events-none">
              {{ button.text }}
            </p>
          </template>
        </RoomieChipSelector>
      </div>
    </section>

    <section class="app-chat__bubbles app-chat__author-bot mt-24 text-pine-600 referral-details">
      <p v-sanitize="t('secondGreetingMessage')" />
    </section>
  </section>
</template>

<i18n src="./i18n.json" lang="json" />
